import { MenuAlt2Icon, XIcon } from '@heroicons/react/outline';
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { siteMap } from '../../sitemap';
import { useAuthState } from '../../utilities/contexts/auth-state-context';
import logo from "../../assets/images/symmio-logos/symmio-icon-logo-light.png";
import UserUtil from '../../utilities/user-util';
import OrganizationUtil from '../../utilities/organization-util';
// import { ReactComponent as AcademyIcon } from "../../assets/icons/nav/icon_academy.svg";
import { OrganizationSubMenu } from './organization-sub-menu';
import { ReportsSubMenu } from './reports-sub-menu';

const COMPONENT_CLASS = "c-sidebar";
// const ACADEMY_ICON_NAME = "sitemap.academy";

const Sidebar: React.FC = () => {
    let authRoutes = siteMap.filter(x => x.includeInNav === true).sort((a, b) => (a.navOrder || 0) - (b.navOrder || 0));
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [activeSubMenu, setActiveSubMenu] = useState<null | 'Report' | 'Super Admin'>(null);
    const [subMenuStyle, setSubMenuStyle] = useState<React.CSSProperties>({});

    const reportsTriggerRef = useRef<HTMLDivElement>(null);
    const reportsMenuRef = useRef<HTMLDivElement>(null);
    const orgTriggerRef = useRef<HTMLDivElement>(null);
    const orgMenuRef = useRef<HTMLDivElement>(null);
    const closeTimeoutRef = useRef<number | null>(null);

    const { state } = useAuthState();
    const sidebarRef = useRef<HTMLDivElement>(null);

    if (UserUtil.isSuperAdmin(state.claims)) {
        authRoutes = authRoutes.filter(x => x.name !== "Org Mgmt");
    }
    else if (UserUtil.isAccountHolder(state.user)) {
        authRoutes = authRoutes.filter(x => !x.onlySuperadmin);

        // Org Mgmt (sub-organization management) is an Enterprise /
        // Enterprise Trial feature; sub-organizations themselves carry no plan,
        // so viewing one still counts as supported.
        if (!OrganizationUtil.supportsSubOrganizations(state.organization)) {
            authRoutes = authRoutes.filter(x => x.name !== "Org Mgmt");
        }
    }

    if (!state.claims) {
        authRoutes = authRoutes.filter(x => x.onlySuperadmin == null || !x.onlySuperadmin);
        authRoutes = authRoutes.filter(x => x.requiredRoles == null || x.requiredRoles.length === 0);
    } else {
        if (!state.claims.superAdmin && !UserUtil.isAccountHolder(state.user)) {
            authRoutes = authRoutes.filter(x => x.onlySuperadmin == null || !x.onlySuperadmin);

            // Admins granted access to a sub-organization keep their admin
            // routes while switched into that sub-organization.
            const isViewingAccessibleOrg = UserUtil.canAccessOrganization(state.user, state.organization?.id);

            if (isViewingAccessibleOrg) {
                authRoutes = authRoutes.filter(x => x.requiredRoles == null || x.requiredRoles.filter(r => state.user && state.user.roles && state.user.roles.includes(r)).length > 0);
            } else {
                authRoutes = authRoutes.filter(x => x.requiredRoles == null || x.requiredRoles.length === 0);
            }
        }
    }

    useEffect(() => {
        document.addEventListener('touchstart', handleClickOutside);

        // Remove event listener when the component unmounts
        return () => {
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (!activeSubMenu || !shouldUseOutsideTapToClose()) return;

        const handlePointerDown = (event: PointerEvent) => {
            const target = event.target as Node;

            const insideReportsTrigger = reportsTriggerRef.current?.contains(target);
            const insideReportsMenu = reportsMenuRef.current?.contains(target);
            const insideOrgTrigger = orgTriggerRef.current?.contains(target);
            const insideOrgMenu = orgMenuRef.current?.contains(target);

            if (
                insideReportsTrigger ||
                insideReportsMenu ||
                insideOrgTrigger ||
                insideOrgMenu
            ) {
                return;
            }
            setActiveSubMenu(null);
        };

        document.addEventListener('pointerdown', handlePointerDown);

        return () => {
            document.removeEventListener('pointerdown', handlePointerDown);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSubMenu]);

    function handleClickOutside(event: Event) {
        if (sidebarRef.current && !sidebarRef.current.contains(event.target as HTMLBaseElement)) {
            // Clicked outside the side navigation bar, close it
            setSidebarOpen(false);
        }
    }

    const getIconAndLabel = (item: any) => {
        return (
            <div>
                {<item.icon />}
                <span>{item.translationName}</span>
            </div>
        )
    }
    const positionMenu = (
        triggerRef: React.RefObject<HTMLDivElement>,
        menuRef: React.RefObject<HTMLDivElement>
    ) => {
        const trigger = triggerRef.current;
        const menu = menuRef.current;
        if (!trigger || !menu) return;

        const triggerRect = trigger.getBoundingClientRect();
        const menuRect = menu.getBoundingClientRect();

        const viewportPadding = 16;
        const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;

        const desiredTop = triggerRect.top;
        const maxTop = window.innerHeight - menuRect.height - viewportPadding;
        const safeTop = Math.max(viewportPadding, Math.min(desiredTop, maxTop));

        setSubMenuStyle({
            position: 'fixed',
            left: isMobile ? '100px' : '100px',
            top: `${safeTop}px`,
            zIndex: 99999,
            pointerEvents: 'auto',
        });
    };

    const waitForMenuLayout = (menuRef: any, callback: any) => {
        let attempts = 0;

        const check = () => {
            const menu = menuRef.current;
            if (menu && menu.getBoundingClientRect().height > 0) {
                callback();
            } else if (attempts < 10) {
                attempts++;
                requestAnimationFrame(check);
            }
        };

        check();
    };

    const openMenu = (menu: 'Report' | 'Super Admin') => {
        clearCloseTimeout();
        setActiveSubMenu(menu);

        waitForMenuLayout(
            menu === 'Report' ? reportsMenuRef : orgMenuRef,
            () => {
                positionMenu(
                    menu === 'Report' ? reportsTriggerRef : orgTriggerRef,
                    menu === 'Report' ? reportsMenuRef : orgMenuRef
                );
            }
        );

        requestAnimationFrame(() => {
            if (menu === 'Report') {
                positionMenu(reportsTriggerRef, reportsMenuRef);
            } else {
                positionMenu(orgTriggerRef, orgMenuRef);
            }
        });
    };

    const closeMenu = () => {
        clearCloseTimeout();
        setActiveSubMenu(null);
    };
    const MOBILE_BREAKPOINT = 767.98;

    const canUseHover = () => {
        return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    };

    const shouldUseOutsideTapToClose = () => {
        return !canUseHover();
    };

    const clearCloseTimeout = () => {
        if (closeTimeoutRef.current) {
            window.clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
    };

    const scheduleClose = () => {
        clearCloseTimeout();
        closeTimeoutRef.current = window.setTimeout(() => {
            setActiveSubMenu(null);
        }, 200);
    };

    return (
        <>
            <div className={`${COMPONENT_CLASS}`} ref={sidebarRef}>
                {
                    !sidebarOpen ?
                        <button
                            type="button"
                            className={`${COMPONENT_CLASS}__mobile-button-open show-on-mobile`}
                            onClick={() => setSidebarOpen(true)}>
                            <span className="sr-only show-on-mobile">Open sidebar</span>
                            <MenuAlt2Icon aria-hidden="true" />
                        </button>
                        :
                        <button
                            type="button"
                            className={`${COMPONENT_CLASS}__mobile-button-close show-on-mobile`}
                            onClick={() => setSidebarOpen(false)}>
                            <span className="sr-only show-on-mobile">Close sidebar</span>
                            <XIcon aria-hidden="true" />
                        </button>
                }
                <div className={`${COMPONENT_CLASS}__nav-container show-on-desktop ${sidebarOpen ? 'show-on-mobile' : ""}`}>
                    <div className={`${COMPONENT_CLASS}__inner`}>
                        <div className={`${COMPONENT_CLASS}__col`}>
                            <div className={`${COMPONENT_CLASS}__logo`}>
                                <a href="/">
                                    <img
                                        src={logo}
                                        alt="Logo" />
                                </a>
                            </div>
                            <div className={`${COMPONENT_CLASS}__nav-list`}>
                                <nav className={`${COMPONENT_CLASS}__nav-list-item`}>
                                    {authRoutes.map((item) => {

                                        const isSuperAdminMenu = item.name === "Super Admin";
                                        const isReportsMenu = item.name === "Report";
                                        const hasSubMenu = isSuperAdminMenu || isReportsMenu;
                                        const linkClass = `
        ${window.location.pathname === item.path ? '-active' : ''}
        ${hasSubMenu ? 'has-submenu' : ''}
        ${isSuperAdminMenu ? 'superadmin' : ''}
    `.trim();

                                        return (
                                            <React.Fragment key={item.name}>

                                                {isReportsMenu ? (
                                                    <div
                                                        ref={reportsTriggerRef}
                                                        className={`sub-menu-only ${linkClass}`}
                                                        onMouseEnter={() => { if (canUseHover()) openMenu('Report'); }}
                                                        onMouseLeave={() => {
                                                            if (canUseHover()) scheduleClose();
                                                        }}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();

                                                            if (activeSubMenu === 'Report') {
                                                                closeMenu();
                                                            } else {
                                                                openMenu('Report');
                                                            }
                                                        }}
                                                    >
                                                        {item.icon ? getIconAndLabel(item) : item.translationName}
                                                    </div>
                                                ) : isSuperAdminMenu ? (
                                                    <div
                                                        ref={orgTriggerRef}
                                                        className={`sub-menu-only ${linkClass}`}
                                                        onMouseEnter={() => { if (canUseHover()) openMenu('Super Admin'); }}
                                                        onMouseLeave={() => {
                                                            if (canUseHover()) scheduleClose();
                                                        }}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();

                                                            if (activeSubMenu === 'Super Admin') {
                                                                closeMenu();
                                                            } else {
                                                                openMenu('Super Admin');
                                                            }
                                                        }}
                                                    >
                                                        {item.icon ? getIconAndLabel(item) : item.translationName}
                                                    </div>
                                                ) : (
                                                    <Link
                                                        className={linkClass}
                                                        to={item.path}
                                                    >
                                                        {item.icon ? getIconAndLabel(item) : item.translationName}
                                                    </Link>
                                                )}



                                            </React.Fragment>
                                        );
                                    })}


                                </nav>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {activeSubMenu === 'Super Admin' && (
                <OrganizationSubMenu
                    menuRef={orgMenuRef}
                    style={subMenuStyle}
                    onMouseEnter={clearCloseTimeout}
                    onMouseLeave={() => {
                        if (canUseHover()) scheduleClose();
                    }}
                />
            )}
            {activeSubMenu === 'Report' && (
                <ReportsSubMenu
                    menuRef={reportsMenuRef}
                    style={subMenuStyle}
                    onMouseEnter={clearCloseTimeout}
                    onMouseLeave={() => {
                        if (canUseHover()) scheduleClose();
                    }}
                />
            )}
        </>
    );
}

export { Sidebar };

